/**
 * API RTK Query pour les emplois du temps d'examens.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ============================================================================
// INTERFACES
// ============================================================================

/** Grille tarifaire de facturation des copies (fix 2026-08-31) — dépend du
 *  déploiement PN ou BZ. */
export interface GrilleFacturation {
  deployment: 'pn' | 'bz';
  type_grille: 'unitaire_avec_bonus_min' | 'forfait_puis_unitaire';
  label: string;
  // PN uniquement
  prix_copie?: number;
  seuil_bonus?: number;
  bonus_montant?: number;
  // BZ uniquement
  forfait_seuil?: number;
  forfait_montant?: number;
  prix_copie_au_dela?: number;
}

/** Réponse commune pour budget_reel et budget_estimatif d'une session. */
export interface BudgetSessionResponse {
  session: string;
  annee_academique: string;
  type: 'reel' | 'estimatif';
  grille: GrilleFacturation;
  marge_pourcentage?: number;     // budget_estimatif uniquement
  type_formation?: string;         // budget_estimatif uniquement
  total_copies: number;
  total_montant: number;
  total_bonus: number;
  nb_formateurs: number;
  formateurs: Array<{
    enseignant_id: number;
    nom: string;
    matieres: Array<{
      nom: string; niveau: string;
      classes?: string[];         // budget_reel uniquement
      copies?: number;             // budget_reel
      copies_estim?: number;       // budget_estimatif
    }>;
    total_copies: number;
    montant: number;
    bonus: number;
    montant_total: number;
    forfait_atteint?: boolean;     // BZ : true si ≤26 copies (forfait 2000)
  }>;
}

export interface ContrainteEmploiTemps {
  id?: number;
  creneaux_horaires: { heure_debut: string; heure_fin: string; label: string }[];
  max_matieres_par_jour: number;
  max_matieres_samedi: number;
  max_matieres_l3: number;
  jour1_une_seule_matiere: boolean;  // DEPRECATED, voir nb_matieres_jour1
  nb_matieres_jour1?: number;
  jour1_heure_debut: string;
  /** Rattrapage : seuil min d'étudiants par matière pour qu'elle soit planifiée */
  min_candidats_rattrapage?: number;
  l3_heure_debut: string;
  composer_dimanche: boolean;
  composer_samedi: boolean;
  /** Jours fériés à exclure de la planification (dates ISO "YYYY-MM-DD") */
  jours_feries?: string[];
  affectation_sites: Record<string, number[]>;
  durees_personnalisees: Record<string, string>;
  alternance_poles: boolean;
  alternance_l2_l3: boolean;
  skip_jour_apres_premier: boolean;
  separer_fi_cs: boolean;
  type_formation: 'FI' | 'CS';
  /** Filtres persistés — utilisés si l'appel ne les redéfinit pas */
  niveaux_filtre?: string[];
  poles_filtre?: string[];
  /** 'L1' ou 'L2L3' — qui compose le jour 1 en cas d'alternance */
  groupe_premier_jour?: 'L1' | 'L2L3';
  /** Répartition manuelle : {date_iso: ['L1'] | ['L2','L3'] | ['L1','L2','L3']} */
  repartition_jours_manuelle?: Record<string, string[]>;
  /** Composition des groupes de niveaux qui composent ensemble.
   *  Ex : [['L1'], ['L2','L3']], [['L1','L2'], ['L3']], [['L1','L2','L3']]
   *  Vide = fallback L1 vs L2/L3 selon alternance_poles. */
  composition_niveaux?: string[][];
  /** Durée de la pause entre 2 matières consécutives (minutes). Défaut 60. */
  duree_pause_minutes?: number;
}

export interface SessionExamen {
  id: number;
  nom: string;
  annee_academique: string;
  semestre: number;
  type_session: 'ordinaire' | 'rattrapage';
  type_formation?: 'FI' | 'CS';
  date_debut: string;
  date_fin: string;
  statut: 'brouillon' | 'genere' | 'modifie' | 'publie' | 'archive';
  genere_par: string;
  date_creation: string;
  date_modification: string;
  nombre_creneaux: number;
  nombre_jours_utilises: number;
  nombre_conflits: number;
  visible_aux_etudiants?: boolean;
  date_visibilite?: string | null;
  visibilite_par_nom?: string;
  contraintes?: ContrainteEmploiTemps;
  nombre_groupes?: number;
  /** 2026-09-14 : 'manuel' = grille vide remplie à la main (génération désactivée) */
  mode_conception?: 'automatique' | 'manuel';
}

export interface SessionExamenCreate {
  nom: string;
  annee_academique: string;
  semestre: number;
  type_session: string;
  type_formation?: 'FI' | 'CS';
  date_debut: string;
  date_fin: string;
  contraintes?: Partial<ContrainteEmploiTemps>;
  mode_conception?: 'automatique' | 'manuel';
}

export interface GroupeOptions {
  id: number;
  label: string;
  niveau: string;
  pole_codes: string[];
  option_codes: string[];
  option_noms: string[];
  ordre_affichage: number;
}

export interface CreneauMatiereData {
  matiere_nom: string;
  enseignant_noms: string[];
  matiere_ids: number[];
  creneau_id?: number;
  est_inactif?: boolean;
  heure_fin?: string;
  duree_label?: string | null;
}

export interface CreneauHoraire {
  heure_label: string;
  /** « HH:MM » (2026-09-14) */
  heure_debut?: string;
  heure_fin?: string;
  matieres: Record<number, CreneauMatiereData>;
}

export interface JourEmploiTemps {
  date: string;
  jour_semaine: string;
  date_label: string;
  creneaux: CreneauHoraire[];
}

export interface EmploiTempsComplet {
  session_id: number;
  niveau: string;
  poles: string[];
  titre: string;
  sous_titre: string;
  semestre: string;
  type_session: string;
  annee_academique: string;
  periode: string;
  groupes: {
    id: number;
    label: string;
    option_codes: string[];
    option_noms: string[];
  }[];
  jours: JourEmploiTemps[];
  statistiques: {
    nombre_groupes: number;
    nombre_jours: number;
    nombre_creneaux: number;
  };
}

// ─── Conception manuelle (2026-09-14) ───────────────────────────────────────
export interface ColonneMatiereAPlacer {
  groupe_id: number;
  label: string;
  pole_key: string;
  option_codes: string[];
  matiere_ids: number[];
  nom: string;
  place: boolean;
  creneau_id: number | null;
  date: string | null;
  heure_debut: string | null;
  heure_fin: string | null;
}

export interface GroupeMatiereAPlacer {
  cle: string;
  niveau: string;
  nom: string;
  poles: string[];
  nb_options: number;
  colonnes: ColonneMatiereAPlacer[];
  options_sans_colonne: string[];
  nb_colonnes: number;
  nb_placees: number;
  complete: boolean;
  /** Noms fusionnés pour l'EDT (saisis différemment d'une option à l'autre) */
  variantes: string[];
}

export interface MatieresAPlacerResponse {
  session_id: number;
  mode_conception: 'automatique' | 'manuel';
  pole: string | null;
  nb_groupes_options: number;
  colonnes_manquantes: boolean;
  total_a_placer: number;
  niveaux: {
    niveau: string;
    total: number;
    nb_completes: number;
    a_placer: GroupeMatiereAPlacer[];
    placees: GroupeMatiereAPlacer[];
    hors_programme: { nom: string; niveau: string; nb_creneaux: number }[];
  }[];
}

export interface AvertissementConception {
  type: 'limite_jour' | 'enseignant';
  message: string;
}

export interface ConflitConception {
  groupe_id: number;
  colonne: string;
  matiere: string;
  horaire: string;
}

export interface ResultatConception {
  success?: boolean;
  a_confirmer?: boolean;
  avertissements?: AvertissementConception[];
  nb_creneaux?: number;
  nb_deplaces?: number;
  nb_retires?: number;
  nb_verrouilles?: number;
  colonnes?: string[];
  heure_fin?: string;
}

export interface FicheDoublonMatiere {
  cle: string;
  noms: string[];
  options: string[];
}

export interface SuggestionDoublon {
  niveau: string;
  pole: string;
  confiance: 'certain' | 'probable' | 'possible';
  raison: string;
  matieres: FicheDoublonMatiere[];
}

export interface FusionMatieres {
  niveau: string;
  pole: string;
  cle: string;
  noms: string[];
  options: string[];
}

export interface DoublonsMatieresResponse {
  suggestions: SuggestionDoublon[];
  fusions: FusionMatieres[];
  ignorees: SuggestionDoublon[];
}

export interface NiveauPole {
  niveau: string;
  pole: string;
}

export interface MatiereNonPlacee {
  nom: string;
  groupes: string[];
  option_codes: string[];
}

export interface NiveauResultat {
  groupes: number;
  matieres: number;
  matieres_total: number;
  matieres_placees: number;
  matieres_non_placees: number;
  creneaux: number;
  detail_non_placees: MatiereNonPlacee[];
  taux_placement: number;
}

export interface ChargeJour {
  effectif: number;
  capacite: number;
  taux: number;
  marge: number;
  label: 'leger' | 'moyen' | 'sature';
  nb_creneaux: number;
  details_matieres?: { matiere: string; effectif: number; heure_debut: string }[];
}

export interface JourEmpruntable {
  date: string;
  jour_semaine: string;
  date_label: string;
  slots: number;
  source: string;
  cible: string;
  charge?: ChargeJour;
}

export interface RepartitionJours {
  alternance_active: boolean;
  l1: { date: string; jour_semaine: string; date_label: string; slots: number; est_premier: boolean }[];
  l2_l3: { date: string; jour_semaine: string; date_label: string; slots: number; est_premier: boolean }[];
  total_slots_l1: number;
  total_slots_l2_l3: number;
}

export interface Suggestion {
  type: string;
  message: string;
  nouvelle_date_fin?: string;
  niveau?: string;
  matieres?: string[];
  source_groupe?: string;
  cible_groupe?: string;
  jours_empruntables?: JourEmpruntable[];
  /** Pour les suggestions rattrapage avec action automatique */
  priorite?: 'haute' | 'moyenne' | 'basse';
  action_payload?: Record<string, unknown>;
  action_label?: string;
}

export interface RapportGeneration {
  total_matieres: number;
  total_placees: number;
  total_non_placees: number;
  taux_placement_global: number;
  criticite: 'ok' | 'critique';
  suggestions: Suggestion[];
}

export interface JourEmprunteDetail {
  date: string;
  jour_semaine: string;
  date_label: string;
  source: string;
  cible: string;
  niveaux: string[];
  matieres: string[];
  nb_matieres: number;
}

export interface JoursEmpruntesInfo {
  nombre: number;
  details: JourEmprunteDetail[];
}

export interface JourEmprunteVerification {
  date: string;
  jour_semaine: string;
  date_label: string;
  source: string;
  cible: string;
  niveau_cible: string;
  matieres: string[];
  statut: 'ok' | 'erreur' | 'en_attente';
  message: string;
  etudiants_concernes?: number;
  salles_habituelles?: { salle: string; site: string; capacite: number; effectif: number }[];
}

export interface VerificationJoursEmpruntesResult {
  applicable: boolean;
  brassage_existe?: boolean;
  brassage_info?: { id: number; date_generation: string; nombre_etudiants: number };
  jours_empruntes: JourEmprunteVerification[];
  alertes: { type: 'warning' | 'error' | 'info'; titre: string; message: string }[];
  conseil?: string;
  message?: string;
}

export interface CalendrierJour {
  date: string;
  date_label: string;
  jour_semaine: string;
  niveaux: string[];
  nb_matieres: number;
}

export interface EnseignantManquantPost {
  matiere: string;
  niveau: string;
  date: string;
  groupe: string;
}

export interface GenerationResultat {
  success: boolean;
  resultats: Record<string, NiveauResultat>;
  total_creneaux: number;
  jours_utilises: number;
  erreurs: string[];
  avertissements: string[];
  rapport?: RapportGeneration;
  jours_empruntes?: JoursEmpruntesInfo;
  calendrier?: CalendrierJour[];
  enseignants_manquants?: EnseignantManquantPost[];
}

export interface PreValidationGroupe {
  label: string;
  nb_matieres: number;
  options: string[];
}

export interface PreValidationAnalyse {
  matieres_total: number;
  slots_disponibles: number;
  deficit: number;
  groupes: PreValidationGroupe[];
}

export interface EnseignantManquantPre {
  niveau: string;
  matiere: string;
  option: string;
}

export interface PerteJour1 {
  date: string;
  date_label: string;
  jour_semaine: string;
  slots_jour1: number;
  slots_normal: number;
  perte: number;
}

export interface DureePersonnalisee {
  matiere: string;
  duree: string;
}

export interface AnalyseRattrapage {
  mode: 'fusion' | 'alternance';
  raison: string;
  effectifs: { L1: number; L2: number; L3: number };
  effectif_total_a_rattraper: number;
  capacite_totale_salles: number;
  capacite_suffit_simultanement: boolean;
  jours_disponibles: number;
  creneaux_par_jour: number;
  matieres_max_par_colonne_l1: number;
  matieres_max_par_colonne_l2l3: number;
  jours_requis_l1_seul: number;
  jours_requis_l2l3_seul: number;
  jours_requis_fusion: number;
  fusion_faisable: boolean;
  besoins_total_creneaux_l1: number;
  besoins_total_creneaux_l2l3: number;
  emprunts_auto?: number;
  jours_l1_planifies?: string[];
  jours_l2l3_planifies?: string[];
}

export interface PreValidationResult {
  peut_generer: boolean;
  analyse: Record<string, PreValidationAnalyse>;
  jours_disponibles: number;
  creneaux_par_jour: number;
  suggestions: Suggestion[];
  avertissements: string[];
  repartition_jours?: RepartitionJours;
  analyse_rattrapage?: AnalyseRattrapage | null;
  contraintes_actives?: string[];
  enseignants_manquants?: EnseignantManquantPre[];
  options_vides?: { niveau: string; option: string }[];
  perte_jour1?: PerteJour1;
  durees_personnalisees?: DureePersonnalisee[];
}

export interface EstimationOptionDetail {
  option: string;
  effectif: number;
}

export interface EstimationCreneauDetail {
  creneau_id: number;
  date: string;
  heure: string;
  matiere: string;
  niveau: string;
  groupe: string;
  pole_codes: string[];
  options: EstimationOptionDetail[];
  effectif: number;
  copies_avec_marge: number;
  formateurs?: { enseignant_id: number; nom: string }[];
}

export interface EstimationNiveauResume {
  niveau: string;
  effectif: number;
  copies_avec_marge: number;
  nb_creneaux: number;
}

export interface EstimationJourResume {
  date: string;
  effectif: number;
  copies_avec_marge: number;
  nb_creneaux: number;
  matieres: string[];
}

export interface EstimationCopiesResponse {
  session: string;
  annee_academique: string;
  semestre: number;
  marge_pourcentage: number;
  type_formation: string;
  total_effectif: number;
  total_copies_avec_marge: number;
  nombre_creneaux: number;
  resume_par_niveau: EstimationNiveauResume[];
  resume_par_jour: EstimationJourResume[];
  details: EstimationCreneauDetail[];
}

export interface AlerteEnseignant {
  matiere_id: number;
  code: string;
  nom: string;
  niveau: string;
  option: string;
  semestre: number;
  ue: string;
  nb_enseignants: number;
  enseignants: { enseignant_id: number; nom: string; specialite: string }[];
  dans_emploi_du_temps: boolean;
  severite: 'warning' | 'info';
}

export interface AlertesEnseignantsResponse {
  alertes: AlerteEnseignant[];
  total_alertes: number;
  dans_emploi: number;
  annee_academique: string;
}

export interface StatistiquesSession {
  total_creneaux: number;
  total_jours: number;
  total_groupes: number;
  par_niveau: Record<string, {
    creneaux: number;
    matieres: number;
    jours: number;
    groupes: number;
  }>;
}

export interface VerificationDetail {
  niveau: string;
  semestre: number;
  groupe: string;
  option: string;
  matieres_attendues: number;
  matieres_planifiees: number;
  complet: boolean;
  manquantes: string[];
  en_trop: string[];
}

export interface VerificationResultat {
  complet: boolean;
  total_options: number;
  options_completes: number;
  options_incompletes: number;
  details: VerificationDetail[];
}

export interface RecapSalleJour {
  salle_id: number;
  salle_nom: string;
  site_nom: string;
  salle_actif: boolean;
  capacite: number;
  effectif: number;
  taux_occupation: number;
}

export interface RecapSiteJour {
  site_nom: string;
  effectif: number;
  capacite: number;
  nombre_salles: number;
  taux_occupation: number;
}

export interface RecapJour {
  date: string;
  jour_semaine: string;
  date_label: string;
  niveaux: string[];
  poles: string[];
  nombre_salles: number;
  total_etudiants: number;
  total_capacite: number;
  taux_occupation_global: number;
  par_site: RecapSiteJour[];
  salles: RecapSalleJour[];
}

export interface RecapSalleGlobal {
  salle_id: number;
  salle_nom: string;
  site_nom: string;
  salle_actif: boolean;
  capacite: number;
  effectif: number;
  par_niveau: Record<string, number>;
}

export interface AlerteCapacite {
  type: 'warning' | 'error' | 'info';
  titre: string;
  message: string;
}

export interface ActionSuggeree {
  type: 'alternance_l2_l3' | 'alternance_poles' | 'reactiver_salle';
  label: string;
  description: string;
  salle_id?: number;
}

export interface SalleInactive {
  salle_id: number;
  salle_nom: string;
  site_nom: string;
  effectif: number;
  capacite: number;
}

// Statistiques de participation
export interface StatGroupData {
  inscrits: number;
  presents: number;
  complets: number;
  partiels: number;
  absents: number;
  taux: number;
}

export interface StatClasseData extends StatGroupData {
  niveau: string;
  pole: string;
}

export interface AbsentPartielDetail {
  etudiant_id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  niveau: string;
  pole: string;
  option: string;
  classe: string;
  type_formation: string;
  sexe: string;
  telephone: string;
  statut: 'absent' | 'partiel';
  nb_prevues: number;
  nb_passees: number;
  nb_manquees: number;
  taux_individuel: number;
  matieres_manquees: string[];
  matieres_passees: string[];
}

export interface AnalyseMatiere {
  matiere: string;
  nb_inscrits: number;
  nb_presents: number;
  nb_absents: number;
  taux_absence: number;
}


export interface StatClasseDataExtended extends StatGroupData {
  niveau: string;
  pole: string;
  option: string;
}

export interface StatistiquesParticipationResponse {
  session: { id: number; nom: string; type_session: string; date_debut: string; date_fin: string; annee_academique: string };
  global: {
    total_inscrits: number;
    total_presents: number;
    total_complets: number;
    total_partiels: number;
    total_absents: number;
    taux_participation: number;
  };
  par_pole: Record<string, StatGroupData>;
  par_niveau: Record<string, StatGroupData>;
  par_classe: Record<string, StatClasseDataExtended>;
  par_option: Record<string, StatClasseDataExtended>;
  analyse_matieres: AnalyseMatiere[];
  suggestions: any[];
  absents_totaux: AbsentPartielDetail[];
  absents_partiels: AbsentPartielDetail[];
  absents_et_partiels: AbsentPartielDetail[];
}

export interface RecapSallesResponse {
  session_id: number;
  session_brassage_id: number;
  nombre_total_etudiants: number;
  nombre_total_salles: number;
  nombre_total_sites: number;
  etudiants_non_places: number;
  jours: RecapJour[];
  toutes_salles: RecapSalleGlobal[];
  brassage_obsolete: boolean;
  alertes: AlerteCapacite[];
  actions_suggerees: ActionSuggeree[];
  salles_inactives: SalleInactive[];
}

export interface ReadapterBrassageResponse {
  session_brassage_id: number;
  session_examen_id: number;
  nombre_etudiants: number;
  nombre_salles_utilisees: number;
  nombre_sites_utilises: number;
  etudiants_non_places: number;
  resultats_par_groupe: {
    niveau: string;
    pole: string;
    etudiants: number;
    places: number;
    non_places: number;
    vague?: string;
    sites?: string[];
    salles?: number;
    capacite?: number;
    erreur?: string;
  }[];
  avertissements: string[];
  message: string;
}

export interface MatiereManquante {
  matiere_id: number | null;
  matiere_ids: number[];
  code: string;
  nom: string;
  niveau: string;
  options: string[];
  est_en_commun: boolean;
  groupes_manquants?: { id: number; label: string }[];
  source?: 'creneau';  // Matière issue des créneaux existants, pas de la table matieres
}

export interface MatiereManquantesResponse {
  session_id: number;
  matieres_manquantes: MatiereManquante[];
  total: number;
}

export interface AjouterMatierePayload {
  session_id: number;
  matiere_id?: number | null;
  matiere_nom?: string;
  niveau?: string;
  groupe_ids?: number[];
  date: string;
  heure_debut: string;
  heure_fin: string;
}

export interface AjouterMatiereResponse {
  success?: boolean;
  warning?: boolean;
  message: string;
  est_en_commun?: boolean;
  options_concernees?: string[];
  creneaux_crees?: CreneauExamen[];
  groupes_affectes?: { id: number; label: string }[];
  enseignant_noms?: string[];
  conflits?: { groupe: string; matiere_existante: string }[];
  creneaux_existants?: CreneauExamen[];
}

export interface CreneauExamen {
  id: number;
  date: string;
  heure_debut: string;
  heure_fin: string;
  heure_label: string;
  jour_semaine: string;
  matiere_nom: string;
  matiere_ids: number[];
  groupe_options: number;
  groupe_options_label: string;
  option_codes: string[];
  pole_codes: string[];
  enseignant_noms: string[];
  niveau: string;
  est_verrouille: boolean;
  commentaire: string;
}

// ============================================================================
// API
// ============================================================================

export const emploisTempsApi = createApi({
  reducerPath: 'emploisTempsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Session', 'EmploiTemps', 'Creneau'],
  endpoints: (builder) => ({

    // ===== SESSIONS =====

    getSessions: builder.query<SessionExamen[], { annee_academique?: string; semestre?: number }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.annee_academique) searchParams.set('annee_academique', params.annee_academique);
        if (params.semestre) searchParams.set('semestre', String(params.semestre));
        return `/emplois-temps/sessions/?${searchParams.toString()}`;
      },
      providesTags: ['Session'],
    }),

    getSession: builder.query<SessionExamen, number>({
      query: (id) => `/emplois-temps/sessions/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Session', id }],
    }),

    createSession: builder.mutation<SessionExamen, SessionExamenCreate>({
      query: (body) => ({
        url: '/emplois-temps/sessions/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Session'],
    }),

    updateSession: builder.mutation<SessionExamen, { id: number; data: Partial<SessionExamenCreate> }>({
      query: ({ id, data }) => ({
        url: `/emplois-temps/sessions/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Session', 'EmploiTemps'],
    }),

    deleteSession: builder.mutation<void, number>({
      query: (id) => ({
        url: `/emplois-temps/sessions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Session'],
    }),

    // ===== PRÉ-VALIDATION & GÉNÉRATION =====

    preValidation: builder.query<PreValidationResult, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/pre_validation/`,
    }),

    genererEmploiTemps: builder.mutation<GenerationResultat, {
      sessionId: number;
      jours_empruntes?: { date: string; source: string; cible: string }[];
      niveaux_filtre?: string[];
      poles_filtre?: string[];
    }>({
      query: ({ sessionId, jours_empruntes, niveaux_filtre, poles_filtre }) => {
        const body: any = {};
        if (jours_empruntes && jours_empruntes.length) body.jours_empruntes = jours_empruntes;
        if (niveaux_filtre && niveaux_filtre.length) body.niveaux_filtre = niveaux_filtre;
        if (poles_filtre && poles_filtre.length) body.poles_filtre = poles_filtre;
        return {
          url: `/emplois-temps/sessions/${sessionId}/generer/`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Session', 'EmploiTemps', 'Creneau'],
    }),

    getDonneesEtiquettes: builder.query<{
      brassage_disponible: boolean;
      brassage_id: number | null;
      brassage_date: string | null;
      // Liste des brassages agrégés dans cette réponse (traçabilité UI)
      brassages_utilises?: Array<{
        id: number; date: string; mode: string;
        nombre_etudiants: number; description: string;
      }>;
      brassage_id_force?: string | null;
      // Tous les brassages de l'année pour le sélecteur "Utiliser brassage : …"
      brassages_disponibles?: Array<{
        id: number; date: string; mode: string;
        nombre_etudiants: number; nombre_sites_utilises: number;
        description: string;
      }>;
      sites_disponibles: { id: number; nom: string }[];
      etiquettes: Array<{
        key: string;
        matiere: string;
        date: string;
        heure_debut: string;
        heure_fin: string;
        niveaux: string[];
        poles: string[];
        classes: string[];  // codes compacts type L1AU, L2MI
        options: string[];  // noms longs (compat)
        formateurs: { id: number; nom_complet: string; telephone: string }[];
        sites: { site_id: number; site_nom: string; effectif: number; source: 'brassage' | 'aucun' }[];
        effectif_total: number;
        creneau_ids: number[];
      }>;
    }, { sessionId: number; brassageId?: number }>({
      query: ({ sessionId, brassageId }) => {
        const qs = brassageId ? `?brassage_id=${brassageId}` : '';
        return `/emplois-temps/sessions/${sessionId}/donnees-etiquettes/${qs}`;
      },
      providesTags: ['Creneau'],
    }),

    ajouterJourSession: builder.mutation<{
      success: boolean;
      date: string;
      changements: string[];
      date_debut: string;
      date_fin: string;
    }, { sessionId: number; date: string }>({
      query: ({ sessionId, date }) => ({
        url: `/emplois-temps/sessions/${sessionId}/ajouter-jour/`,
        method: 'POST',
        body: { date },
      }),
      invalidatesTags: ['Session', 'EmploiTemps', 'Creneau'],
    }),

    ajouterHoraireSession: builder.mutation<{
      success: boolean;
      creneau_ajoute: { heure_debut: string; heure_fin: string; label: string };
      creneaux_horaires: { heure_debut: string; heure_fin: string; label: string }[];
      message: string;
    }, { sessionId: number; heure_debut: string; heure_fin: string; label?: string }>({
      query: ({ sessionId, heure_debut, heure_fin, label }) => ({
        url: `/emplois-temps/sessions/${sessionId}/ajouter-horaire/`,
        method: 'POST',
        body: { heure_debut, heure_fin, label },
      }),
      invalidatesTags: ['Session', 'EmploiTemps', 'Creneau'],
    }),

    supprimerHoraireSession: builder.mutation<{
      success: boolean;
      creneaux_horaires: { heure_debut: string; heure_fin: string; label: string }[];
      nb_creneaux_supprimes: number;
    }, { sessionId: number; heure_debut: string; force?: boolean }>({
      query: ({ sessionId, heure_debut, force }) => ({
        url: `/emplois-temps/sessions/${sessionId}/supprimer-horaire/`,
        method: 'POST',
        body: { heure_debut, force: force ? true : undefined },
      }),
      invalidatesTags: ['Session', 'EmploiTemps', 'Creneau'],
    }),

    synchroniserCommunes: builder.mutation<{
      success: boolean;
      matieres_alignees: number;
      creneaux_deplaces: number;
      conflits: any[];
      avertissements: string[];
    }, number>({
      query: (sessionId) => ({
        url: `/emplois-temps/sessions/${sessionId}/synchroniser-communes/`,
        method: 'POST',
      }),
      invalidatesTags: ['Session', 'EmploiTemps', 'Creneau'],
    }),

    diagnosticMatieresDesync: builder.query<{
      session_id: number; session_libelle: string;
      total_matieres_desynchronisees: number;
      total_multi_orthographes: number;
      total_multi_enseignants: number;
      total_bloques: number;
      groupes: Array<{
        nom_normalise: string;
        nom_affiche: string;
        noms_originaux: string[];
        multi_orthographes: boolean;
        enseignants: string[];
        meme_enseignant: boolean;
        slots_distincts: number;
        creneaux: Array<{
          id: number; groupe_id: number; groupe_label: string; niveau: string;
          date: string; heure_debut: string; heure_fin: string;
          verrouille: boolean; sur_slot_canonique: boolean;
        }>;
        slot_canonique_propose: {
          date: string; heure_debut: string; heure_fin: string; nb_creneaux_deja_dessus: number;
        };
        blocages: any[];
        peut_etre_aligne_automatiquement: boolean;
      }>;
    }, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/diagnostic-matieres-desync/`,
      providesTags: ['EmploiTemps', 'Creneau'],
    }),

    synchroniserMatiereUnique: builder.mutation<{
      success: boolean; creneaux_deplaces: number;
      conflits: any[];
      slot_canonique: { date: string; heure_debut: string; heure_fin: string };
    }, { sessionId: number; nom_normalise: string; slot_cible_id?: number }>({
      query: ({ sessionId, ...body }) => ({
        url: `/emplois-temps/sessions/${sessionId}/synchroniser-matiere/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Session', 'EmploiTemps', 'Creneau'],
    }),

    regenererHoraires: builder.mutation<
      { success: boolean; creneaux_horaires: any[]; duree_pause_minutes: number; creneaux_mis_a_jour: number },
      { sessionId: number; duree_pause_minutes: number; duree_matiere_minutes?: number; heure_debut?: string; nb_creneaux?: number; appliquer_aux_creneaux?: boolean }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/emplois-temps/sessions/${sessionId}/regenerer-horaires/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Session', 'EmploiTemps', 'Creneau'],
    }),

    toggleVisibiliteSession: builder.mutation<
      { success: boolean; id: number; visible_aux_etudiants: boolean; date_visibilite: string | null; visibilite_par_nom: string },
      { sessionId: number; visible?: boolean }
    >({
      query: ({ sessionId, visible }) => ({
        url: `/emplois-temps/sessions/${sessionId}/toggle-visibilite/`,
        method: 'POST',
        body: visible !== undefined ? { visible } : {},
      }),
      invalidatesTags: ['Session'],
    }),

    // ===== EMPLOIS DU TEMPS =====

    getEmploiTemps: builder.query<EmploiTempsComplet, { sessionId: number; niveau: string; pole: string }>({
      query: ({ sessionId, niveau, pole }) =>
        `/emplois-temps/sessions/${sessionId}/emploi_temps/?niveau=${niveau}&pole=${pole}`,
      providesTags: ['EmploiTemps'],
    }),

    getTousEmplois: builder.query<EmploiTempsComplet[], number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/tous_emplois/`,
      providesTags: ['EmploiTemps'],
    }),

    getNiveauxDisponibles: builder.query<NiveauPole[], number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/niveaux_disponibles/`,
      providesTags: ['EmploiTemps'],
    }),

    // ===== CONCEPTION MANUELLE (2026-09-14) =====

    getEmploiTempsGrilleManuelle: builder.query<EmploiTempsComplet, { sessionId: number; niveau: string; pole: string }>({
      query: ({ sessionId, niveau, pole }) =>
        `/emplois-temps/sessions/${sessionId}/emploi_temps/?niveau=${niveau}&pole=${pole}&vides=1`,
      providesTags: ['EmploiTemps'],
    }),

    getMatieresAPlacer: builder.query<MatieresAPlacerResponse, { sessionId: number; pole?: string }>({
      query: ({ sessionId, pole }) =>
        `/emplois-temps/sessions/${sessionId}/matieres-a-placer/${pole ? `?pole=${encodeURIComponent(pole)}` : ''}`,
      providesTags: ['EmploiTemps'],
    }),

    getDoublonsMatieres: builder.query<DoublonsMatieresResponse, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/doublons-matieres/`,
      providesTags: ['EmploiTemps'],
    }),

    modifierCorrespondanceMatieres: builder.mutation<
      { success: boolean; action: string },
      { sessionId: number; action: 'fusionner' | 'separer' | 'ignorer' | 'retablir'; niveau: string; pole: string; cles: string[] }
    >({
      query: ({ sessionId, ...body }) => ({
        url: `/emplois-temps/sessions/${sessionId}/correspondances-matieres/`, method: 'POST', body,
      }),
      invalidatesTags: ['EmploiTemps'],
    }),

    preparerGrille: builder.mutation<
      { success: boolean; groupes_crees: { id: number; niveau: string; label: string }[]; nb_groupes: number },
      number
    >({
      query: (sessionId) => ({ url: `/emplois-temps/sessions/${sessionId}/preparer-grille/`, method: 'POST' }),
      invalidatesTags: ['EmploiTemps', 'Session'],
    }),

    placerMatiere: builder.mutation<ResultatConception, {
      sessionId: number; niveau: string; cle: string; date: string;
      heure_debut: string; heure_fin: string; groupe_ids?: number[]; confirmer?: boolean; pole?: string;
    }>({
      query: ({ sessionId, ...body }) => ({
        url: `/emplois-temps/sessions/${sessionId}/placer-matiere/`, method: 'POST', body,
      }),
      invalidatesTags: (r) => (r?.a_confirmer ? [] : ['EmploiTemps', 'Session', 'Creneau']),
    }),

    retirerMatiere: builder.mutation<ResultatConception, {
      sessionId: number; niveau: string; cle: string; date?: string; heure_debut?: string; groupe_ids?: number[]; pole?: string;
    }>({
      query: ({ sessionId, ...body }) => ({
        url: `/emplois-temps/sessions/${sessionId}/retirer-matiere/`, method: 'POST', body,
      }),
      invalidatesTags: ['EmploiTemps', 'Session', 'Creneau'],
    }),

    deplacerMatiere: builder.mutation<ResultatConception, {
      sessionId: number; niveau: string; cle: string; date: string; heure_debut: string;
      vers_date: string; vers_heure_debut: string; vers_heure_fin?: string; groupe_ids?: number[]; confirmer?: boolean; pole?: string;
    }>({
      query: ({ sessionId, ...body }) => ({
        url: `/emplois-temps/sessions/${sessionId}/deplacer-matiere/`, method: 'POST', body,
      }),
      invalidatesTags: (r) => (r?.a_confirmer ? [] : ['EmploiTemps', 'Session', 'Creneau']),
    }),

    getStatistiques: builder.query<StatistiquesSession, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/statistiques/`,
      providesTags: ['Session'],
    }),

    // ===== VÉRIFICATION =====

    verifierCompletude: builder.query<VerificationResultat, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/verifier_completude/`,
      providesTags: ['EmploiTemps'],
    }),

    verifierJoursEmpruntes: builder.query<VerificationJoursEmpruntesResult, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/verifier_jours_empruntes/`,
      providesTags: ['EmploiTemps'],
    }),

    // ===== CRÉNEAUX (ajustement manuel) =====

    getCreneaux: builder.query<CreneauExamen[], { session?: number; niveau?: string }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.session) searchParams.set('session', String(params.session));
        if (params.niveau) searchParams.set('niveau', params.niveau);
        return `/emplois-temps/creneaux/?${searchParams.toString()}`;
      },
      providesTags: ['Creneau'],
    }),

    deplacerCreneau: builder.mutation<CreneauExamen, {
      creneau_id: number;
      nouvelle_date: string;
      nouvelle_heure_debut?: string;
      nouvelle_heure_fin?: string;
    }>({
      query: (body) => ({
        url: '/emplois-temps/creneaux/deplacer/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmploiTemps', 'Creneau'],
    }),

    modifierHorairesJour: builder.mutation<
      {
        success: boolean;
        date: string;
        nb_modifies: number;
        nb_total_jour: number;
        details: { ancien: string; nouveau_debut: string; nouveau_fin: string; nb_creneaux: number }[];
        message: string;
      },
      {
        session_id: number;
        date: string;
        mappings: { ancienne_heure_debut: string; nouvelle_heure_debut: string; nouvelle_heure_fin: string }[];
        ignorer_verrouilles?: boolean;
      }
    >({
      query: (body) => ({
        url: '/emplois-temps/creneaux/modifier_horaires_jour/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmploiTemps', 'Creneau', 'Session'],
    }),

    deplacerGroupe: builder.mutation<{ success: boolean; deplace: number; message: string }, {
      matiere_nom: string;
      date_source: string;
      heure_source: string;
      nouvelle_date: string;
      nouvelle_heure_debut: string;
      nouvelle_heure_fin: string;
      session_id: number;
      niveau: string;
    }>({
      query: (body) => ({
        url: '/emplois-temps/creneaux/deplacer_groupe/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmploiTemps', 'Creneau'],
    }),

    echangerGroupes: builder.mutation<{ success: boolean; echanges: number; message: string }, {
      session_id: number;
      niveau: string;
      matiere_a_nom: string; date_a: string; heure_a: string;
      matiere_b_nom: string; date_b: string; heure_b: string;
      /** IDs des groupes à swapper — si fourni, restreint le swap à ces colonnes. */
      groupe_ids?: number[];
    }>({
      query: (body) => ({
        url: '/emplois-temps/creneaux/echanger-groupes/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmploiTemps', 'Creneau'],
    }),

    verrouillerCreneau: builder.mutation<{ verrouille: boolean }, number>({
      query: (id) => ({
        url: `/emplois-temps/creneaux/${id}/verrouiller/`,
        method: 'POST',
      }),
      invalidatesTags: ['Creneau'],
    }),

    deleteCreneau: builder.mutation<void, number>({
      query: (id) => ({
        url: `/emplois-temps/creneaux/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmploiTemps', 'Creneau'],
    }),

    toggleInactif: builder.mutation<{ est_inactif: boolean; matiere_nom: string }, number>({
      query: (id) => ({
        url: `/emplois-temps/creneaux/${id}/toggle_inactif/`,
        method: 'POST',
      }),
      invalidatesTags: ['EmploiTemps', 'Creneau'],
    }),

    getEstimationCopies: builder.query<EstimationCopiesResponse, { sessionId: number; marge?: number; type_formation?: string }>({
      query: ({ sessionId, marge, type_formation }) => {
        const params = new URLSearchParams();
        if (marge !== undefined) params.set('marge', String(marge));
        if (type_formation) params.set('type_formation', type_formation);
        return `/emplois-temps/sessions/${sessionId}/estimation_copies/?${params.toString()}`;
      },
      providesTags: ['EmploiTemps'],
    }),

    getBudgetReel: builder.query<BudgetSessionResponse, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/budget_reel/`,
      providesTags: ['EmploiTemps'],
    }),

    getBudgetEstimatif: builder.query<BudgetSessionResponse, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/budget_estimatif/`,
      providesTags: ['EmploiTemps'],
    }),

    getAlertesEnseignants: builder.query<AlertesEnseignantsResponse, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/alertes_enseignants/`,
      providesTags: ['EmploiTemps'],
    }),

    modifierEnseignantCreneau: builder.mutation<
      { success: boolean; enseignant_noms: string[]; matiere_nom: string },
      { creneauId: number; enseignant_id?: number | null; retirer_index?: number; enseignant_noms?: string[]; annee_academique?: string }
    >({
      query: ({ creneauId, ...body }) => ({
        url: `/emplois-temps/creneaux/${creneauId}/modifier_enseignant/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmploiTemps', 'Creneau'],
    }),

    // ===== RECAP SALLES =====

    getRecapSalles: builder.query<RecapSallesResponse, number>({
      query: (sessionId) => `/emplois-temps/sessions/${sessionId}/recap_salles/`,
      providesTags: ['Session'],
    }),

    toggleSalleActif: builder.mutation<{ id: number; nom: string; site_nom: string; actif: boolean }, number>({
      query: (salleId) => ({
        url: `/emplois-temps/salles/${salleId}/toggle-actif/`,
        method: 'POST',
      }),
      invalidatesTags: ['Session'],
    }),

    readapterBrassage: builder.mutation<ReadapterBrassageResponse, number>({
      query: (sessionId) => ({
        url: `/emplois-temps/sessions/${sessionId}/readapter_brassage/`,
        method: 'POST',
      }),
      invalidatesTags: ['Session'],
    }),

    // ===== AJOUT MATIÈRE MANQUANTE =====

    getMatiereManquantes: builder.query<
      MatiereManquantesResponse,
      { sessionId: number; niveau?: string }
    >({
      query: ({ sessionId, niveau }) => {
        const params = new URLSearchParams({ session_id: String(sessionId) });
        if (niveau) params.set('niveau', niveau);
        return `/emplois-temps/creneaux/matieres_manquantes/?${params.toString()}`;
      },
      providesTags: ['EmploiTemps'],
    }),

    ajouterMatiere: builder.mutation<AjouterMatiereResponse, AjouterMatierePayload>({
      query: (body) => ({
        url: '/emplois-temps/creneaux/ajouter_matiere/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmploiTemps', 'Creneau', 'Session'],
    }),

    fusionnerGroupes: builder.mutation<
      {
        success: boolean;
        groupe_principal: { id: number; label: string; option_codes: string[] };
        groupes_absorbes: string[];
        nb_reassignes: number;
        nb_doublons_fusionnes: number;
        message: string;
      },
      { groupe_ids: number[] }
    >({
      query: (body) => ({
        url: '/emplois-temps/groupes-options/fusionner/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EmploiTemps', 'Session', 'Creneau'],
    }),

    getStatistiquesParticipation: builder.query<StatistiquesParticipationResponse, { session_id: number; cycle?: 'licence' | 'master' }>({
      query: (params) => ({
        url: '/statistiques-participation/',
        params,
      }),
    }),
  }),
});

export const {
  useGetSessionsQuery,
  useGetSessionQuery,
  useCreateSessionMutation,
  useUpdateSessionMutation,
  useDeleteSessionMutation,
  useLazyPreValidationQuery,
  useGenererEmploiTempsMutation,
  useGetDonneesEtiquettesQuery,
  useAjouterJourSessionMutation,
  useAjouterHoraireSessionMutation,
  useSupprimerHoraireSessionMutation,
  useSynchroniserCommunesMutation,
  useDiagnosticMatieresDesyncQuery,
  useLazyDiagnosticMatieresDesyncQuery,
  useSynchroniserMatiereUniqueMutation,
  useRegenererHorairesMutation,
  useToggleVisibiliteSessionMutation,
  useGetEmploiTempsQuery,
  useGetTousEmploisQuery,
  useGetNiveauxDisponiblesQuery,
  useGetEmploiTempsGrilleManuelleQuery,
  useGetMatieresAPlacerQuery,
  useGetDoublonsMatieresQuery,
  useModifierCorrespondanceMatieresMutation,
  usePreparerGrilleMutation,
  usePlacerMatiereMutation,
  useRetirerMatiereMutation,
  useDeplacerMatiereMutation,
  useGetStatistiquesQuery,
  useVerifierCompletudeQuery,
  useLazyVerifierJoursEmpruntesQuery,
  useGetCreneauxQuery,
  useDeplacerCreneauMutation,
  useDeplacerGroupeMutation,
  useEchangerGroupesMutation,
  useModifierHorairesJourMutation,
  useVerrouillerCreneauMutation,
  useDeleteCreneauMutation,
  useToggleInactifMutation,
  useModifierEnseignantCreneauMutation,
  useGetEstimationCopiesQuery,
  useGetBudgetReelQuery,
  useLazyGetEstimationCopiesQuery,
  useGetAlertesEnseignantsQuery,
  useGetRecapSallesQuery,
  useToggleSalleActifMutation,
  useReadapterBrassageMutation,
  useGetMatiereManquantesQuery,
  useLazyGetMatiereManquantesQuery,
  useAjouterMatiereMutation,
  useFusionnerGroupesMutation,
  useGetStatistiquesParticipationQuery,
} = emploisTempsApi;
